import { useMemo } from 'react';
import { PlanEntitlementKey } from '../../api/graphql/generated';
import { useMachineContext } from '../../pages/machine/machine-context-provider';
import { useHasEntitlementLazy } from '../plan/plan-entitlement-hooks';
import { useHasAssignedPlanLicenseLazy } from '../plan/plan-hooks';

export interface ParameterPaywallOptions {
  planEntitlementKey?: PlanEntitlementKey;
}

export interface ParameterPaywallResult {
  isPaywalled: boolean;
  requiresPlanEntitlement: boolean;
  planEntitlementKey?: PlanEntitlementKey;
}

/**
 * Hook to determine if a parameter is available based on paywall options
 * Handles both plan entitlements and machine license requirements
 */
export const useParameterPaywall = (
  paywallOptions?: ParameterPaywallOptions
): ParameterPaywallResult => {
  const { planEntitlementKey } = paywallOptions ?? {};
  const { machine } = useMachineContext();
  const hasEntitlement = useHasEntitlementLazy();
  const hasAssignedPlanLicense = useHasAssignedPlanLicenseLazy();

  const isTenantEntitled = useMemo(() => {
    if (!planEntitlementKey) {
      return true;
    }
    return hasEntitlement(planEntitlementKey);
  }, [hasEntitlement, planEntitlementKey]);

  const isMachineLicensed = useMemo(() => {
    if (!planEntitlementKey) {
      return true;
    }
    return hasAssignedPlanLicense({
      key: planEntitlementKey,
      assignedTo: machine.id,
    });
  }, [hasAssignedPlanLicense, planEntitlementKey, machine.id]);

  return useMemo<ParameterPaywallResult>(() => {
    if (!planEntitlementKey) {
      return {
        isPaywalled: false,
        requiresPlanEntitlement: false,
        planEntitlementKey: undefined,
      };
    }

    const isPaywalled = !isTenantEntitled || !isMachineLicensed;

    return {
      isPaywalled,
      requiresPlanEntitlement: !!planEntitlementKey,
      planEntitlementKey,
    };
  }, [planEntitlementKey, isTenantEntitled, isMachineLicensed]);
};
